import {
  useEffectWithController,
  useSubsequentEffect
} from '@carvana/merch-react-hooks';
import { useContext, useRef } from 'react';
import { MAX_PAGE_SIZE_DEFAULT } from '~/constants/srp';
import AppliedFiltersContext from '~/context/common/AppliedFiltersContext';
import FacetDataContext from '~/context/common/FacetDataContext';
import { useIsServerDriven } from '~/context/srp/SrpNavModeContext';
import useGenerateInventoryRequest from '~/hooks/srp/useGenerateInventoryRequest';
import fetchFacetData from '~/services/common/fetchFacetData';
import { generateErrorLogger } from '~/utilities/loggers';

type Options = {
  // The mobile filter modal previews a staged, un-navigated filter set no RSC island
  // covers, so it must client-fetch counts even in server-driven mode.
  previewFetch?: boolean;
};

export default function useGetFacetData(
  facetDataWasFetched?: boolean,
  { previewFetch = false }: Options = {}
): void {
  const { appliedFilters } = useContext(AppliedFiltersContext);
  const {
    facetData: { setData, setError, setLoading },
    setPagination
  } = useContext(FacetDataContext);
  const isServerDriven = useIsServerDriven();
  const useClientFetch = !isServerDriven || previewFetch;
  const isFirstRenderAndFacetDataWasFetched = useRef(
    facetDataWasFetched ?? true
  );

  const { request } = useGenerateInventoryRequest(MAX_PAGE_SIZE_DEFAULT, {
    includePageNumber: false,
    limitRequestedFeatures: true
  });

  // cars-rsc gates off the client fetch below, so reproduce its setLoading() here on a
  // filter change so consumers waiting on the load transition behave like control.
  const appliedFiltersKey = JSON.stringify(appliedFilters ?? {});
  useSubsequentEffect(() => {
    if (isServerDriven && !previewFetch) setLoading();
  }, [appliedFiltersKey, isServerDriven, previewFetch, setLoading]);

  useEffectWithController(
    controller => {
      // cars-rsc streams committed facets via an RSC island; only the modal's
      // staged preview fetch runs client-side.
      if (!useClientFetch) return;

      if (isFirstRenderAndFacetDataWasFetched.current) {
        isFirstRenderAndFacetDataWasFetched.current = false;

        return;
      }

      setLoading();

      fetchFacetData(
        {
          ...request
        },
        controller
      )
        .then(({ data: { pagination, ...facets } }) => {
          setData(facets);
          setPagination(pagination);
        })
        .catch(
          generateErrorLogger(
            'Facet data',
            {},
            { callback: setError, controller }
          )
        );
    },
    [request, setData, setError, setLoading, setPagination, useClientFetch]
  );
}
